import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { ChangeSalesPriceModal } from "@/components/admin/shared/ChangeSalesPriceModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import { PageHeader, SaveButton, CancelButton, ViewButton, EditButton, DeleteButton, AddButton } from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import {
  profilIndieOptionSchema,
  type ProfilIndieOptionFormData,
  PROFIL_INDIE_ELEMENT_TYPES,
} from "@/types/admin/profilIndieOptions.types";
import {
  useProfileIndieOptions,
  useProfileIndieOption,
  useCreateProfileIndieOption,
  useUpdateProfileIndieOption,
  useDeleteProfileIndieOption,
} from "@/hooks/useProfileIndieOptions";
import {
  useApplyDatasetPriceUpdate,
  useDatasetPriceUpdateSummary,
} from "@/hooks/usePriceUpdate";
import type { ProfileIndieOption } from "@/api/profileIndieOptions";
import { formatMoney, type Currency } from "@/utils/dataset-money";
import { Percent } from "lucide-react";

type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: ProfileIndieOption } | null;

/** The element-type columns a page renders (region-specific — see types file). */
type ElementTypes = readonly string[];

const zeroPrices = (elementTypes: ElementTypes): ProfilIndieOptionFormData["prices"] =>
  Object.fromEntries(elementTypes.map((k) => [k, 0]));

const blankForm = (elementTypes: ElementTypes): ProfilIndieOptionFormData => ({
  optionName: "",
  subCategory: "",
  prices: zeroPrices(elementTypes),
});

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

/** Decimal-string price → number for the form input. Empty/null becomes 0. */
const priceToNumber = (v: string | null | undefined): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Filter to non-zero / non-null entries, ordered by the page's element-type list. */
const nonZeroPriceEntries = (
  prices: Record<string, string | null>,
  elementTypes: ElementTypes,
): Array<[string, string]> =>
  elementTypes
    .map((k) => [k, prices[k]] as const)
    .filter(([, v]) => v !== null && v !== undefined && v !== "" && Number(v) !== 0)
    .map(([k, v]) => [k, v as string]);

/** 2-col key/value grid shared by the hover tooltip and the click popover.
 *  Prices render 2dp en-US via the shared `dataset-money` formatter (same as
 *  Sherex Options); `title` keeps the untouched numeric(12,6) string (§L). */
const PriceGrid = ({
  entries,
  currency,
}: {
  entries: Array<[string, string]>;
  currency: Currency;
}) => {
  if (entries.length === 0) {
    return <div className="text-blue_grey4 italic text-small">No prices set</div>;
  }
  return (
    <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-small">
      {entries.map(([k, v]) => (
        <Fragment key={k}>
          <span className="font-mono text-blue_grey3">{k}</span>
          <span className="font-semibold text-blue_grey text-right tabular-nums" title={v}>
            {formatMoney(v, currency)}
          </span>
        </Fragment>
      ))}
    </div>
  );
};

/** Hover tooltip + click-to-pin popover for the Prices cell. */
const PricesCell = ({
  prices,
  elementTypes,
  currency,
}: {
  prices: Record<string, string | null>;
  elementTypes: ElementTypes;
  currency: Currency;
}) => {
  const entries = useMemo(() => nonZeroPriceEntries(prices, elementTypes), [prices, elementTypes]);
  const [hovering, setHovering] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  // Recompute popover position relative to the trigger; clamp to viewport.
  const reposition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const POP_W = 200;
    const POP_H = Math.min(320, 24 + entries.length * 22);
    const margin = 8;
    let left = r.left;
    let top = r.bottom + 6;
    if (left + POP_W + margin > window.innerWidth) left = window.innerWidth - POP_W - margin;
    if (left < margin) left = margin;
    if (top + POP_H + margin > window.innerHeight) top = r.top - POP_H - 6;
    if (top < margin) top = margin;
    setCoords({ top, left });
  };

  useEffect(() => {
    if (!pinned && !hovering) return;
    reposition();
    const onScrollOrResize = () => reposition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinned, hovering]);

  // Outside-click closes the pinned popover.
  useEffect(() => {
    if (!pinned) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setPinned(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [pinned]);

  const open = pinned || hovering;
  const cellLabel = entries.length > 0 ? `${entries.length} ${entries.length === 1 ? "price" : "prices"}` : "—";

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onClick={(e) => { e.stopPropagation(); setPinned((p) => !p); }}
        className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-0.5 text-blue_grey hover:border-penn-500 hover:bg-penn-50 dark:hover:bg-penn-900/30 transition text-small-medium"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-penn-500" />
        {cellLabel}
      </button>

      {open && coords && createPortal(
        <div
          ref={popRef}
          role="dialog"
          onMouseEnter={() => pinned || setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          style={{ position: "fixed", top: coords.top, left: coords.left, width: 200, zIndex: 9999 }}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg p-3"
        >
          <PriceGrid entries={entries} currency={currency} />
        </div>,
        document.body,
      )}
    </>
  );
};

export const ProfilIndieOptionsPage = ({
  title = "Dataset: Profil InDie Options",
  subtitle = "Record-level editor for Profil InDie options.",
  recordLabel = "Profil InDie Options",
  regionId = 1,
  elementTypes = PROFIL_INDIE_ELEMENT_TYPES,
}: {
  title?: string;
  subtitle?: string;
  /** Noun used in the add/edit/view modal titles. */
  recordLabel?: string;
  /** 1 = EU (default), 2 = US. Scopes all list/detail/create/update calls. */
  regionId?: number;
  /** Element-type price columns rendered by this page (region-specific). */
  elementTypes?: ElementTypes;
}) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));

  const params = useMemo(
    () => ({ page: pageIndex + 1, limit: pageSize, regionId, search: search || undefined }),
    [pageIndex, pageSize, regionId, search],
  );

  const { data, isLoading, isFetching, isError, error } = useProfileIndieOptions(params);
  const rows: ProfileIndieOption[] = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProfileIndieOption | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Bulk "% change bulk price" dialog state. Unlike the PEMSERTER price list
  // there is no derived companion column here — the only thing the change
  // touches is `profil_indi_options_prices.price`. History is written to the
  // same `penn_config.price_update_log` table, tagged with this dataset+region.
  const [priceChangeOpen, setPriceChangeOpen] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const priceSummary = useDatasetPriceUpdateSummary(
    "profil-indie-options",
    regionId,
    priceChangeOpen,
  );
  const applyPriceMutation = useApplyDatasetPriceUpdate("profil-indie-options");

  const applyPriceChange = (percentage: number, direction: "increase" | "decrease") => {
    setBulkError(null);
    applyPriceMutation.mutate(
      { percentage, direction, regionId },
      {
        onSuccess: () => setPriceChangeOpen(false),
        onError: (e) => setBulkError(apiErrorMessage(e, "Failed to apply price change.")),
      },
    );
  };

  const editingOptionName = modal?.mode !== "add" ? (modal?.item?.optionName ?? null) : null;
  const detail = useProfileIndieOption(editingOptionName, regionId);

  const createMutation = useCreateProfileIndieOption();
  const updateMutation = useUpdateProfileIndieOption();
  const deleteMutation = useDeleteProfileIndieOption();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ProfilIndieOptionFormData>({
    resolver: zodResolver(profilIndieOptionSchema),
  });

  const fillForm = (item: ProfileIndieOption) => {
    const prices = Object.fromEntries(
      elementTypes.map((k) => [k, priceToNumber(item.pricesByElementType[k])]),
    );
    reset({
      optionName: item.optionName,
      subCategory: item.subCategory ?? "",
      prices,
    });
  };

  const openAdd = () => { setFormError(null); reset(blankForm(elementTypes)); setModal({ mode: "add" }); };
  const openEdit = (item: ProfileIndieOption) => { setFormError(null); fillForm(item); setModal({ mode: "edit", item }); };
  const openView = (item: ProfileIndieOption) => { setFormError(null); fillForm(item); setModal({ mode: "view", item }); };
  const closeModal = () => { setModal(null); setFormError(null); };

  const onSubmit = (form: ProfilIndieOptionFormData) => {
    setFormError(null);
    const pricesByElementType: Record<string, string> = Object.fromEntries(
      elementTypes.map((k) => [k, String(form.prices[k] ?? 0)]),
    );

    if (modal?.mode === "edit" && modal.item) {
      // Preserve any DB-only element_type keys the form doesn't render so we
      // don't drop existing data on edit (the API does a replace-set on PUT).
      const existing = modal.item.pricesByElementType;
      const merged: Record<string, string | null> = { ...existing, ...pricesByElementType };
      updateMutation.mutate(
        {
          optionName: modal.item.optionName,
          body: {
            subCategory: form.subCategory || null,
            regionId,
            pricesByElementType: merged,
          },
        },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to save profile indie option.")),
        },
      );
    } else {
      createMutation.mutate(
        {
          optionName: form.optionName.trim(),
          subCategory: form.subCategory || null,
          regionId,
          pricesByElementType,
        },
        {
          onSuccess: closeModal,
          onError: (e) => setFormError(apiErrorMessage(e, "Failed to create profile indie option.")),
        },
      );
    }
  };

  const onConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(
      { optionName: deleteTarget.optionName, regionId: deleteTarget.regionId },
      { onSettled: () => setDeleteTarget(null) },
    );
  };

  const isView = modal?.mode === "view";
  const saving = createMutation.isPending || updateMutation.isPending;

  const cols: ColDef<ProfileIndieOption>[] = [
    { header: "Name", key: "optionName" },
    { header: "Sub-category", render: (i) => i.subCategory ?? "—" },
    // Currency travels via the row's region (§D.14): the API row carries it
    // ("EUR" for EU/region 1, "USD" for NA/region 2); default EUR if absent.
    { header: "Prices", render: (i) => <PricesCell prices={i.pricesByElementType} elementTypes={elementTypes} currency={i.currency === "USD" ? "USD" : "EUR"} /> },
    { header: "Actions", render: (i) => <div className="flex space-x-2"><ViewButton onClick={() => openView(i)} /><EditButton onClick={() => openEdit(i)} /><DeleteButton onClick={() => setDeleteTarget(i)} /></div> },
  ];

  const titleMap = {
    add: `Add ${recordLabel} record`,
    edit: `Edit ${recordLabel} record`,
    view: `View ${recordLabel} record`,
  };

  return (
    <>
      <PageHeader
        title={title}
        subtitle={`${subtitle} · ${total} records`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => { setBulkError(null); setPriceChangeOpen(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-penn-500 hover:bg-penn-700 active:bg-penn-500 text-white rounded-lg shadow-sm shadow-penn-100 dark:shadow-penn-900/40 transition-all duration-150 cursor-pointer text-body-medium"
            >
               <Percent size={15} />
              Change Bulk Price
            </button>
            <AddButton onClick={openAdd} />
          </div>
        }
      />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load profile indie options{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        onClear={clearSearch}
        placeholder="Search name…"
      />

      {/* part_no is in the key because the US (region 2) dataset has a few
          option_name values repeated across different part numbers — the table
          has no unique constraint, so (optionName, regionId) alone is not
          row-unique there. */}
      <AdminTable columns={cols} rows={rows} keyExtractor={(i) => `${i.optionName}::${i.regionId}::${i.partNo ?? ""}`} loading={isLoading} />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => { setPageSize(size); setPageIndex(0); }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        maxWidth="max-w-2xl"
        footer={
          isView
            ? <CancelButton onClick={closeModal} />
            : <><CancelButton onClick={closeModal} /><SaveButton onClick={handleSubmit(onSubmit)} label={saving ? "Saving…" : "Save"} /></>
        }
      >
        {modal?.mode !== "add" && detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading…</div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
                {formError}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PennField label="Option Name" htmlFor="optionName" required={!isView} error={!isView ? errors.optionName?.message : undefined}>
                <PennInput
                  id="optionName"
                  type="text"
                  disabled={isView || modal?.mode === "edit"}
                  error={!isView && !!errors.optionName}
                  placeholder="Enter option name"
                  {...register("optionName")}
                />
              </PennField>
              <PennField label="Sub-category" htmlFor="subCategory" required={!isView} error={!isView ? errors.subCategory?.message : undefined}>
                <PennInput
                  id="subCategory"
                  type="text"
                  disabled={isView}
                  error={!isView && !!errors.subCategory}
                  placeholder="Enter sub-category"
                  {...register("subCategory")}
                />
              </PennField>
            </div>

            <PennField label="Prices by Element Type" htmlFor="prices">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-1">
                {elementTypes.map((et) => (
                  <PennField
                    key={et}
                    label={et}
                    htmlFor={`prices.${et}`}
                    error={!isView ? errors.prices?.[et]?.message : undefined}
                  >
                    <PennInput
                      id={`prices.${et}`}
                      type="number"
                      step="0.01"
                      disabled={isView}
                      error={!isView && !!errors.prices?.[et]}
                      {...register(`prices.${et}` as const)}
                    />
                  </PennField>
                ))}
              </div>
            </PennField>
          </form>
        )}
      </AdminModal>

      <ChangeSalesPriceModal
        open={priceChangeOpen}
        onClose={() => setPriceChangeOpen(false)}
        title={`% Change Bulk Price — ${recordLabel}`}
        currency={priceSummary.data?.currency ?? "EUR"}
        scopeLabel={recordLabel}
        recordCount={priceSummary.data?.totalRecords ?? 0}
        recordNoun={{ one: "price", many: "prices" }}
        descriptionSuffix="Every element-type price on this dataset is scaled; empty prices stay empty."
        applying={applyPriceMutation.isPending}
        error={bulkError}
        onApply={applyPriceChange}
        lastUpdate={priceSummary.data?.last}
        loadingSummary={priceSummary.isLoading}
      />

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={onConfirmDelete}
        itemLabel={deleteTarget?.optionName}
      />
    </>
  );
};
